<template>
  <main class="service-page">
    <header class="service-hero">
      <p class="brand">清北博雅 · 学员服务说明</p>
      <h1>课程退费办理说明</h1>
      <p class="lead">当您确需办理课程退费时，可按照以下流程提交申请。我们会核对报名与服务情况，向您说明费用核算结果，并在确认手续完成后推进退款处理。</p>
      <div class="hero-note">退费金额、适用条件及处理方式，以您报名课程对应的协议约定、支付记录及实际服务履行情况为依据。</div>
    </header>

    <div class="keypoints">
      <article class="key"><strong>流程看得见</strong><p>从提交申请到安排退款，每一步需要您做什么、我们会反馈什么，均向您说明。</p></article>
      <article class="key"><strong>金额有依据</strong><p>费用核算会结合协议约定与实际服务情况；您可了解计算依据并提出疑问。</p></article>
      <article class="key"><strong>有异议可复核</strong><p>如您对核算方案存在疑问，可先提出复核和说明需求，再决定是否确认方案。</p></article>
    </div>

    <section class="service-section">
      <div class="section-head"><h2>退费办理流程</h2><span class="sub">共 6 步，建议按顺序办理</span></div>
      <div class="steps-line steps-6">
        <article class="step"><div class="num">01</div><h3>提出退费申请</h3><p>向负责老师或教务说明您希望办理退费，并提供基本报名信息。</p></article>
        <article class="step"><div class="num">02</div><h3>核对报名与服务情况</h3><p>核实课程、付款记录、协议约定及已开展的服务情况。</p></article>
        <article class="step"><div class="num">03</div><h3>反馈费用核算方案</h3><p>根据协议及实际履行情况，说明应退金额、计算依据与后续事项。</p></article>
        <article class="step"><div class="num">04</div><h3>确认或提出复核</h3><p>您可确认方案；如有疑问，也可提出复核或补充说明。</p></article>
        <article class="step"><div class="num">05</div><h3>完成退费确认手续</h3><p>方案确认后，核对收款信息并完成必要的退费确认材料。</p></article>
        <article class="step"><div class="num">06</div><h3>安排退款并反馈结果</h3><p>手续完成后安排退款处理，并向您同步相关进度与结果。</p></article>
      </div>
      <div class="duration-box" style="display:flex;align-items:center;gap:22px;margin-top:22px;padding:25px 28px;border-radius:18px;background:#fbf1f2;border:1px solid #e8d0d3;">
        <div class="duration-badge" style="flex:0 0 auto;padding:14px 19px;min-width:177px;border-radius:12px;background:linear-gradient(128deg,#61030d,#8c0b18);color:#fff;text-align:center;"><small style="display:block;font-size:13px;opacity:.93;">预计处理时效</small><strong style="display:block;font-size:28px;line-height:1.2;">3-5个</strong><small style="display:block;font-size:13px;opacity:.93;">工作日内安排退款</small></div>
        <div><h3 style="margin:0 0 5px;color:#61030d;font-size:18px;">时效从什么时候开始计算？</h3><p style="margin:0;color:#65454a;font-size:14px;">在退费方案已经确认、所需材料及收款信息完整、退费确认手续完成后，预计3-5个工作日内安排退款。实际到账时间可能受银行或支付渠道处理进度影响。</p></div>
      </div>
    </section>

    <div class="two-col">
      <div class="card"><h2>您需要准备的信息</h2><ul class="items"><li>报名学员姓名、手机号及所报课程信息</li><li>支付订单、付款凭证或协议材料（如需核验）</li><li>本人可正常接收退款的收款信息</li><li>如对服务情况有疑问，可一并说明具体问题</li></ul></div>
      <div class="card"><h2>费用如何核算</h2><ul class="items"><li>以报名时对应课程协议中的退费约定为基础</li><li>结合实际已提供的课程或相关服务情况进行核对</li><li>向您说明拟退金额及处理依据后，再进行确认</li></ul><div class="calc"><strong>对金额存在疑问怎么办？</strong>您可在确认前提出复核需求，工作人员会结合协议和实际记录向您说明。</div></div>
    </div>

    <section class="service-section">
      <div class="section-head"><h2>办理过程中，您可以了解和确认什么</h2></div>
      <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:13px;">
        <div class="right-card" v-for="r in rights" :key="r.title"><b>{{ r.title }}</b><span>{{ r.desc }}</span></div>
      </div>
    </section>

    <section class="service-section faq">
      <div class="section-head"><h2>常见问题</h2></div>
      <details open><summary>提出退费申请后，是否会直接退款？</summary><p>提出申请后，需要先核对报名课程、协议约定、支付情况及实际服务情况，再向您反馈具体的退费核算方案。方案确认并完成必要手续后，进入退款处理环节。</p></details>
      <details><summary>我不同意核算金额，还能继续沟通吗？</summary><p>可以。如您对退费金额或服务记录存在疑问，可提出具体异议或补充材料，由工作人员进行复核和说明。在双方尚未确认前，沟通仍属于核对处理阶段。</p></details>
      <details><summary>沟通过程中，我决定继续学习怎么办？</summary><p>如您希望继续学习，可向老师说明当前遇到的问题及学习需求。老师可结合课程情况提供相应支持方案，您可自主决定是否继续学习。</p></details>
      <details><summary>为什么到账时间可能与退款安排时间不同？</summary><p>退款安排完成后，实际入账还可能受到银行或原支付渠道处理速度影响。如超过合理时间仍未到账，可联系对接老师协助查询进度。</p></details>
      <div class="bottom-note"><strong>说明：</strong>本页面用于帮助学员了解一般办理步骤。涉及您的具体退费金额、适用条件、材料要求及处理结果时，以双方实际签署的课程协议、退款确认材料及真实处理记录为准。</div>
    </section>
  </main>
</template>

<script>
export default {
  name: "RefundGuide",
  data() {
    return {
      rights: [
        { title: "确认核算依据", desc: "了解退费方案依据的协议条款和实际服务情况。" },
        { title: "提出异议或复核", desc: "对金额或事实存在疑问时，可提出说明和复核需求。" },
        { title: "确认处理进度", desc: "完成确认手续后，可向对接老师了解当前退款进展。" },
      ],
    };
  },
};
</script>

<style lang="scss" scoped>
@import "../../css/service-common.scss";
.service-hero {
  .lead { max-width: 775px; }
}
.right-card {
  padding: 18px 17px; border-radius: 13px; background: #fdfafa; border: 1px solid #ead7d8;
  b { display: block; margin-bottom: 6px; color: #61030d; font-size: 15px; }
  span { display: block; font-size: 13px; color: #6d6062; }
}
</style>
